const CHECKOUT_SELECTOR = 'form.tx-checkout';
const SHIPPING_SECTION_SELECTOR = '[data-tx-shipping-address]';
const SHIPPING_FIELDS_SELECTOR = '.tx-checkout__shipping-address-fields';
const SAME_CONTROL_SELECTOR = '[data-tx-same-billing]';
const SAME_TOGGLE_SELECTOR = '[data-tx-same-toggle]';
const NATIVE_TOGGLE_SELECTOR = '#ship-to-different-address-checkbox';
const SHIP_CARD_SELECTOR = '[data-tx-ship-card]';
const SHIP_CARD_NAME_SELECTOR = '[data-tx-ship-card-name]';
const SHIP_CARD_ADDRESS_SELECTOR = '[data-tx-ship-card-address]';
const CLOSE_AFTER_COPY_MS = 500;
const PANEL_TRANSITION_MS = 380;

const ADDRESS_SUFFIXES = [
    'first_name',
    'last_name',
    'company',
    'country',
    'address_1',
    'address_2',
    'city',
    'state',
    'postcode',
];

const CLEAR_WHEN_DIFFERENT_SUFFIXES = ADDRESS_SUFFIXES.filter((suffix) => suffix !== 'country');

const getField = (form, prefix, suffix) => form.querySelector(`#${prefix}_${suffix}`);

const getFieldValue = (form, prefix, suffix) => {
    const field = getField(form, prefix, suffix);
    return field ? (field.value || '').trim() : '';
};

const getActiveAddressPrefix = (form) => {
    const section = form.querySelector(SHIPPING_SECTION_SELECTOR);
    return section && section.dataset.same === 'false' ? 'shipping' : 'billing';
};

const formatAddressLine = (form, prefix) => {
    const addressParts = [
        getFieldValue(form, prefix, 'address_1'),
        getFieldValue(form, prefix, 'address_2'),
    ].filter(Boolean);

    const locality = [
        getFieldValue(form, prefix, 'city'),
        getFieldValue(form, prefix, 'state'),
        getFieldValue(form, prefix, 'postcode'),
    ].filter(Boolean).join(' ');

    if (locality) {
        addressParts.push(locality);
    }

    return addressParts.join(', ');
};

const updateShipCard = (form) => {
    const card = form.querySelector(SHIP_CARD_SELECTOR);
    if (!card) return;

    const prefix = getActiveAddressPrefix(form);
    const nameEl = card.querySelector(SHIP_CARD_NAME_SELECTOR);
    const addressEl = card.querySelector(SHIP_CARD_ADDRESS_SELECTOR);
    const company = getFieldValue(form, prefix, 'company');
    const fullName = [
        getFieldValue(form, prefix, 'first_name'),
        getFieldValue(form, prefix, 'last_name'),
    ].filter(Boolean).join(' ');
    const address = formatAddressLine(form, prefix);

    if (nameEl) {
        nameEl.textContent = company || fullName || 'Your company';
    }

    if (addressEl) {
        addressEl.textContent = address || 'Address provided at checkout';
    }
};

const dispatchFieldEvents = (field) => {
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));

    if (window.jQuery) {
        window.jQuery(field).trigger('change');
    }
};

const setFieldValue = (field, value) => {
    if (!field || field.value === value) return;
    field.value = value;
    dispatchFieldEvents(field);
};

const copyBillingToShipping = (form) => {
    const copySuffixes = (suffixes) => {
        suffixes.forEach((suffix) => {
            const billingField = getField(form, 'billing', suffix);
            const shippingField = getField(form, 'shipping', suffix);

            if (!billingField || !shippingField) return;
            setFieldValue(shippingField, billingField.value || '');
        });
    };

    copySuffixes(['country']);
    window.setTimeout(() => copySuffixes(ADDRESS_SUFFIXES.filter((suffix) => suffix !== 'country')), 0);
};

const clearShippingFields = (form) => {
    CLEAR_WHEN_DIFFERENT_SUFFIXES.forEach((suffix) => {
        const shippingField = getField(form, 'shipping', suffix);
        setFieldValue(shippingField, '');
    });
};

const syncToggle = (control, sameAsBilling) => {
    const toggle = control.querySelector(SAME_TOGGLE_SELECTOR);
    if (!toggle) return;

    toggle.textContent = sameAsBilling
        ? toggle.dataset.labelSame || 'Different as billing?'
        : toggle.dataset.labelDifferent || 'Same as billing?';
    toggle.setAttribute('aria-expanded', String(!sameAsBilling));
    toggle.setAttribute(
        'aria-label',
        sameAsBilling ? 'Use a different shipping address' : 'Use billing address for shipping',
    );
};

const requestCheckoutUpdate = () => {
    if (window.jQuery) {
        window.jQuery(document.body).trigger('update_checkout');
    }
};

const animateShippingFields = (section, sameAsBilling, immediate = false) => {
    const panel = section.querySelector(SHIPPING_FIELDS_SELECTOR);
    if (!panel) {
        section.dataset.same = String(sameAsBilling);
        return;
    }

    window.clearTimeout(section.txShippingPanelTimer);

    // A collapsed panel must be display:none, not just 0px tall: Stripe skips
    // the payment method when a required row that jQuery sees as :visible is
    // empty, and the shipping fields stay empty while same-as-billing is on.
    if (immediate) {
        section.dataset.same = String(sameAsBilling);
        panel.style.height = '';
        panel.hidden = sameAsBilling;
        return;
    }

    if (sameAsBilling) {
        panel.style.height = `${panel.scrollHeight}px`;
        panel.offsetHeight;
        section.dataset.same = 'true';
        window.requestAnimationFrame(() => {
            panel.style.height = '0px';
        });
    } else {
        panel.hidden = false;
        panel.style.height = '0px';
        section.dataset.same = 'false';
        panel.offsetHeight;
        window.requestAnimationFrame(() => {
            panel.style.height = `${panel.scrollHeight}px`;
        });
    }

    section.txShippingPanelTimer = window.setTimeout(() => {
        panel.style.height = '';
        panel.hidden = sameAsBilling;
    }, PANEL_TRANSITION_MS);
};

const setSameAsBilling = (form, sameAsBilling, options = {}) => {
    const { animate = true, clear = false, copy = false, trigger = true, closeDelay = 0 } = options;
    const section = form.querySelector(SHIPPING_SECTION_SELECTOR);
    const control = form.querySelector(SAME_CONTROL_SELECTOR);
    const nativeToggle = form.querySelector(NATIVE_TOGGLE_SELECTOR);

    if (!section || !control || !nativeToggle) return;

    window.clearTimeout(section.txSameBillingTimer);

    if (clear) {
        clearShippingFields(form);
    }

    if (copy) {
        copyBillingToShipping(form);
    }

    const commit = () => {
        animateShippingFields(section, sameAsBilling, !animate);
        nativeToggle.checked = !sameAsBilling;
        syncToggle(control, sameAsBilling);
        updateShipCard(form);

        if (trigger) {
            requestCheckoutUpdate();
        }
    };

    if (closeDelay > 0) {
        section.txSameBillingTimer = window.setTimeout(commit, closeDelay);
        return;
    }

    commit();
};

const initCheckoutAddresses = (form) => {
    const section = form.querySelector(SHIPPING_SECTION_SELECTOR);
    const control = form.querySelector(SAME_CONTROL_SELECTOR);
    const nativeToggle = form.querySelector(NATIVE_TOGGLE_SELECTOR);

    if (!section || !control || !nativeToggle) return;

    const initialSameAsBilling = section.dataset.same !== 'false';
    setSameAsBilling(form, initialSameAsBilling, { animate: false, trigger: false });

    control.addEventListener('click', (event) => {
        const toggle = event.target.closest(SAME_TOGGLE_SELECTOR);
        if (!toggle) return;

        const sameAsBilling = section.dataset.same !== 'false';

        if (sameAsBilling) {
            setSameAsBilling(form, false, { clear: true });
            return;
        }

        setSameAsBilling(form, true, { copy: true, closeDelay: CLOSE_AFTER_COPY_MS });
    });

    nativeToggle.addEventListener('change', () => {
        const sameAsBilling = !nativeToggle.checked;
        animateShippingFields(section, sameAsBilling);
        syncToggle(control, sameAsBilling);
        updateShipCard(form);
    });
};

const initShipCardPreview = (form) => {
    const card = form.querySelector(SHIP_CARD_SELECTOR);
    if (!card) return;

    form.addEventListener('input', (event) => {
        if (event.target.matches('[id^="billing_"], [id^="shipping_"]')) {
            updateShipCard(form);
        }
    });

    form.addEventListener('change', (event) => {
        if (event.target.matches('[id^="billing_"], [id^="shipping_"], ' + NATIVE_TOGGLE_SELECTOR)) {
            updateShipCard(form);
        }
    });

    if (window.jQuery) {
        window.jQuery(document.body).on('updated_checkout', () => updateShipCard(form));
    }

    updateShipCard(form);
};

const init = () => {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((form) => {
        initCheckoutAddresses(form);
        initShipCardPreview(form);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
